import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Dialog from "Trackalyze/Dialog";
import Surface from "Trackalyze/Surface";
import Flex from "Trackalyze/Flex";
import Button from "Trackalyze/Button";
import TextField from "Trackalyze/TextField";
import { useTranslation } from "react-i18next";
import List, { ListItem } from "Trackalyze/List";
import Toolbar from "Trackalyze/Toolbar";
import DialogToolbar from "Trackalyze/DialogToolbar";
import Typo from "Trackalyze/Typo";
import Icon from "Trackalyze/Icon";

const GroupsEditDialog = ({
  open,
  groups,
  onClose,
  onAfterAnimate,
  onRenameGroup,
  onDelete,
  onRemoveTrack,
  title,
}) => {
  const { t } = useTranslation();
  const [groupNames, setGroupNames] = useState({});
  const [editID, setEditID] = useState(0);
  const [deleteID, setDeleteID] = useState(null);

  useEffect(() => {
    if (open && groups) {
      const initialNames = {};
      groups.forEach((g) => {
        initialNames[g.id] = g.name;
      });
      setGroupNames(initialNames);
    }
  }, [open, groups]);

  const handleClose = () => {
    if (editID !== 0) {
      onRenameGroup(editID, groupNames[editID]);
      setEditID(0);
    }
    onClose();
  };

  const dialogTitle = title || t("import.renameGroupsTitle");

  return createPortal(
    <>
      <Dialog
        size="m"
        animate={open}
        zIndex={2800}
        onAfterAnimate={() =>
          onAfterAnimate ? onAfterAnimate() : handleClose()
        }
        onClose={handleClose}
        className="ta-flex ta-flex--column ta-gap_4"
      >
        <Toolbar onClose={handleClose} title={dialogTitle} />
        <Surface shape="m" level="2" className="ta-flexitem ta-is-overflow">
          <List segmented level="1">
            {groups &&
              groups.map((group) =>
                editID === group.id ? (
                  <Flex
                    key={group.id}
                    itemscenter
                    spacing="2"
                    className="ta-color_surface-container-highest"
                  >
                    <Button
                      text
                      icon
                      symbol="delete"
                      className="ta-margin_left-s"
                      iconProps={{
                        className: "ta-color-text_error",
                      }}
                      toggle
                      onClick={(e) => {
                        if (group.tracks && group.tracks.length === 0) {
                          onDelete(group.id);
                        } else {
                          setDeleteID(group.id);
                        }
                        e.stopPropagation();
                      }}
                    />

                    <TextField
                      placeholder={t("groups.newGroupNamePlaceholder")}
                      value={groupNames[group.id] || ""}
                      onChange={(value) =>
                        setGroupNames((prev) => ({
                          ...prev,
                          [group.id]: value,
                        }))
                      }
                    />
                    <Button
                      text
                      toggle
                      icon
                      symbol="check"
                      onClick={() => {
                        onRenameGroup(group.id, groupNames[group.id]);
                        setEditID(0);
                      }}
                      className="ta-margin_right-s"
                    />
                  </Flex>
                ) : (
                  <ListItem
                    onClick={() => setEditID(group.id)}
                    key={group.id}
                    label={`${groupNames[group.id] || ""} (${group.tracks ? group.tracks.length : group.count || 0})`}
                    leading={
                      <Icon
                        symbol="stack"
                        className="ta-margin_xxs ta-color-text_on-surface-lowest"
                      />
                    }
                    trailing={
                      <Button
                        text
                        icon
                        symbol="edit"
                        toggle
                        onClick={() => setEditID(group.id)}
                      />
                    }
                    xexpandable={
                      <List level="3" segmented>
                        {group.tracks &&
                          group.tracks.map((track) => (
                            <ListItem
                              leading={
                                <Icon
                                  symbol="track"
                                  className="ta-margin_xxs ta-color-text_on-surface-lowest"
                                />
                              }
                              key={track.id}
                              label={track.name}
                              className="ta-padding_left-m"
                              trailing={
                                <Button
                                  text
                                  icon
                                  symbol="link_off"
                                  toggle
                                  onClick={(e) => {
                                    onRemoveTrack(track.id);
                                    e.stopPropagation();
                                  }}
                                />
                              }
                            />
                          ))}
                      </List>
                    }
                  />
                ),
              )}
          </List>
        </Surface>
        <Flex end spacing="4" className="ta-padding_top-s">
          <Button filled onClick={handleClose}>
            {t("import.buttonCloseDone")}
          </Button>
        </Flex>
      </Dialog>
      {deleteID && (
        <Dialog animate={true} onAfterAnimate={() => {}} zIndex={2900}>
          <DialogToolbar
            symbol="delete"
            title={t("groups.deleteTitle")}
            onClose={() => setDeleteID(null)}
          />
          <div className="ta-margin_bottom-m ta-padding_top-xs ta-padding_right-s">
            <Typo>
              {t("groups.deleteInfo", {
                count: groups.find((g) => g.id === deleteID)?.count || 0,
              })}
            </Typo>
          </div>
          <Flex spacing="2" end>
            <Button text onClick={() => setDeleteID(null)}>
              {t("trackManager.deleteButtonCancel")}
            </Button>
            <Button
              error
              onClick={() => {
                onDelete(deleteID);
                setDeleteID(null);
              }}
            >
              {t("groups.buttonUngroup")}
            </Button>
          </Flex>
        </Dialog>
      )}
    </>,
    document.body,
  );
};

export default React.memo(GroupsEditDialog);
